import clsx from 'clsx'
import first from 'lodash/first'
import noop from 'lodash/noop'
import React, {
  createContext,
  FC,
  MutableRefObject,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import SvgClose from '@Assets/SVG/Close'
import { getOverlayContainer } from '@Components/Overlay/useOverlayContainer'
import { enableBodyScroll, preventBodyScroll } from '@Globals/BodyHelpers'
import { useSplitTest } from '@Globals/SplitTests/Hooks'

import styles from './NativeModal.module.scss'

export type NativeModalActions = {
  close: () => void
  open: () => void
  runReloadAnimation: () => void
}

export const useNativeModalActions = () => {
  const ref = useRef<NativeModalActions | null>(null)
  return ref
}

const CloseContext = createContext<(fromOnExit?: boolean) => void >(noop)
export const NATIVE_MODAL_OPEN_CLASS = 'NativeModalOpen'

export interface NativeModalProps {
  actionRef?: MutableRefObject<NativeModalActions | null>
  alignment?: 'left' | 'center' | 'right' | 'top' | 'bottom'
  backdrop?: boolean
  className?: string
  closeOnBackDrop?: boolean
  closeOnEscape?: boolean
  defaultOpen?: boolean
  onClose?: () => void
  onOpen?: () => void
  portal?: boolean
  prerender?: boolean
}

export const NativeModalBody = ({
  barebones = false,
  children,
  className,
}: {
  barebones?: boolean
  children: ReactNode
  className?: string
}) => (
  <div className={clsx(styles.modalBody, className, 'modalBody', barebones && styles.barebones)}>
    <div className={styles.inner}>
      {children}
    </div>
  </div>
)

export const NativeModalCloseButton = () => {
  const close = useContext(CloseContext)
  return (
    <button
      aria-label="Close"
      className={styles.closeButton}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        close(false)
      }}
      type="button"
    >
      <SvgClose />
    </button>
  )
}

export const NativeModalHeader: FC<PropsWithChildren<{ className?: string, float?: boolean }>> = ({
  children,
  className,
  float,
}) => {
  const close = useContext(CloseContext)
  return (
    <div className={clsx(styles.modalHeader, className, 'modalHeader', !children && styles.noChildren, float && styles.float)}>
      {children}
      <button
        aria-label="Close"
        className={styles.close}
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          close(false)
        }}
        type="button"
      >
        <SvgClose />
      </button>
    </div>
  )
}

export const NativeModalFooter = ({
  barebones,
  children,
  className,
}: {
  barebones?: boolean
  children: ReactNode
  className?: string
}) => (
  <div className={clsx(styles.modalFooter, className, barebones && styles.barebones, 'modalFooter')}>
    {children}
  </div>
)

// TODO: Implement proper dialog solution when we can support all IOS versions, Polyfill did not work as of 09/04/2024
export const NativeModal: FC<PropsWithChildren<NativeModalProps>> = ({
  actionRef,
  alignment = 'center',
  backdrop,
  children,
  className,
  closeOnBackDrop = true,
  closeOnEscape = true,
  onClose = noop,
  onOpen = noop,
  defaultOpen = false,
  portal,
  prerender,
}) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const [reloadingState, setReloadingState] = useState(0)
  const closeOnEscapeRef = useRef(closeOnEscape)
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    closeOnEscapeRef.current = closeOnEscape
  }, [closeOnEscape])

  const close = useCallback((fromOnExit?: boolean) => {
    setIsOpen(false)
    setTimeout(() => {
      const dialogs = document.getElementsByClassName(NATIVE_MODAL_OPEN_CLASS)
      if (dialogs.length === 0) {
        enableBodyScroll()
      }
      if (!fromOnExit) {
        onClose()
      }
    }, 50)
  }, [onClose, setIsOpen])

  const initReloading = useCallback(() => {
    // moves it back in the execution stack
    setTimeout(() => setReloadingState(1), 0)
  }, [])

  const open = useCallback(() => {
    preventBodyScroll()
    setIsOpen(true)
    onOpen()
  }, [onOpen, setIsOpen])

  useImperativeHandle(actionRef, () => ({
    open: () => open && open(),
    close: () => close && close(true),
    runReloadAnimation: initReloading,
  }))

  useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true)
      open()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onBackdropClick = (e) => {
    if (reloadingState) {
      return
    }
    if (!closeOnBackDrop || !ref.current) {
      return
    }
    const dialogDimensions = ref.current?.getBoundingClientRect()
    if (
      e.clientX < dialogDimensions.left
      || e.clientX > dialogDimensions.right
      || e.clientY < dialogDimensions.top
      || e.clientY > dialogDimensions.bottom
    ) {
      close()
    }
  }

  useEffect(() => {
    if (isOpen) {
      preventBodyScroll()
    }
  }, [isOpen])

  const onEscapeKey = useCallback((e) => {
    if (closeOnEscapeRef.current && (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27)) {
      close()
    }
  }, [close])

  const initCloseEvents = useCallback(() => {
    if (closeOnEscape) {
      window.addEventListener('keyup', onEscapeKey)
    }
    return () => {
      // element.removeEventListener('click', onBackdropClick)
      window.removeEventListener('keyup', onEscapeKey)
    }
  }, [closeOnEscape, onEscapeKey])

  useEffect(() => {
    if (reloadingState) {
      setTimeout(() => {
        first(document.getElementsByClassName('modalBody'))
          ?.scrollTo({
            top: 0,
            behavior: 'instant',
          })
        setReloadingState(prev => (prev > 2 ? 0 : prev + 1))
      }, 300)
    }
  }, [reloadingState])

  // @ts-ignore
  const content = (
    <div className={clsx(styles.dialogWrapper, backdrop && styles.withBackdrop)} onClick={(e) => { e.stopPropagation() }}>
      {isOpen && backdrop && (
        <div
          className={clsx('backdrop', styles.backdrop)}
          onClick={onBackdropClick}
          onTouchMove={(e) => {
            e.stopPropagation()
          }}
          onTouchStart={(e) => {
            e.stopPropagation()
          }}
        />
      )}
      <CloseContext.Provider value={close}>
        <dialog
          className={clsx(
            className,
            styles.modal,
            'fixed',
            'modal',
            isOpen && styles.isOpen,
            isOpen && NATIVE_MODAL_OPEN_CLASS,
            (prerender && !isOpen) && styles.hidden,
            styles[alignment],
            reloadingState === 1 && styles.reloadingIn,
            reloadingState === 2 && styles.reloadingOut,
            cofDesignV1 && styles.cofDesignV1,
          )}
          open={isOpen}
          ref={(reference) => {
            if (reference) {
              initCloseEvents()
            }
            ref.current = reference!
          }}
        >
          {(isOpen || prerender) && children}
        </dialog>
      </CloseContext.Provider>
    </div>
  )

  if (portal) {
    const overlayContainer = getOverlayContainer()
    if (overlayContainer) {
      return createPortal(content, overlayContainer)
    }
  }

  return content
}
